import { httpClient } from './http-client';
import type { PaginacionInfo } from './api.types';

export interface EstablecimientoAccesoResumen {
  id: string;
  establecimientoId: string;
  establecimientoCodigo?: string;
  establecimientoNombre?: string;
  rolId?: string;
  rolNombre?: string;
  esActivo: boolean;
}

export interface EmpresaResumen {
  id: string;
  ruc: string;
  razonSocial: string;
  nombreComercial?: string;
  esActivo: boolean;
  establecimientos: EstablecimientoAccesoResumen[];
}

export interface Usuario {
  id: string;
  nombre: string;
  apellido: string;
  email: string;
  celular?: string;
  tipoDocumento?: string;
  numeroDocumento?: string;
  esActivo: boolean;
  requiereCambioPassword: boolean;
  usuarioId?: string;
  usuarioNombre?: string;
  establecimientoIds?: string[];
  createdAt: string;
  updatedAt: string;
  empresas?: EmpresaResumen[];
  rolesDistintos?: string[];
}

export interface UsuariosPaginatedResponse {
  data: Usuario[];
  paginacion: PaginacionInfo;
}

export interface UsuarioFiltros {
  search?: string;
  empresaId?: string;
  rol?: string;
  esActivo?: boolean;
  page?: number;
  pageSize?: number;
  sortField?: 'nombre' | 'email' | 'es_activo' | 'created_at';
  sortOrder?: 'asc' | 'desc';
}

export interface CreateUsuarioDTO {
  nombre: string;
  apellido: string;
  email: string;
  celular?: string;
  tipoDocumento?: string;
  numeroDocumento?: string;
  password: string;
  requiereCambioPassword?: boolean;
  esActivo?: boolean;
  usuarioId?: string;
  usuarioNombre?: string;
}

export interface UpdateUsuarioDTO {
  id: string;
  nombre: string;
  apellido: string;
  email: string;
  celular?: string;
  tipoDocumento?: string;
  numeroDocumento?: string;
  esActivo?: boolean;
  usuarioId?: string;
  usuarioNombre?: string;
}

export interface CambioPasswordDTO {
  passwordActual: string;
  passwordNuevo: string;
}

export interface AsignarEstablecimientosDTO {
  usuarioId: string;
  establecimientoIds: string[];
}

export type EstadoSesion = 'Activa' | 'Cerrada' | 'Expirada';

export interface SesionActiva {
  id: string;
  dispositivo: string;
  navegador: string;
  sistemaOperativo: string;
  ip?: string;
  ultimaActividad?: string;
  iniciadaEn: string;
  esSesionActual: boolean;
  estado: EstadoSesion;
  cerradaEn?: string;
}

export interface SesionesPaginatedResponse {
  data: SesionActiva[];
  paginacion: PaginacionInfo;
}

export const usuariosService = {
  getAll: (params?: UsuarioFiltros) => {
    return httpClient.get<Usuario[]>('/usuarios', params as Record<string, string | number | boolean | undefined> | undefined);
  },

  getAllPaginated: async (params?: UsuarioFiltros): Promise<UsuariosPaginatedResponse> => {
    const response = await httpClient.get<Usuario[]>('/usuarios', params as Record<string, string | number | boolean | undefined> | undefined);
    return {
      data: response.data || [],
      paginacion: response.paginacion || {
        page: params?.page || 1,
        pageSize: params?.pageSize || 20,
        total: 0,
        totalPages: 0,
        hasNext: false,
        hasPrevious: false
      }
    };
  },

  getById: (id: string) => {
    return httpClient.get<Usuario>(`/usuarios/${id}`);
  },

  create: (data: CreateUsuarioDTO) => {
    return httpClient.post<Usuario>('/usuarios/create', data);
  },

  update: (data: UpdateUsuarioDTO) => {
    return httpClient.put<Usuario>(`/usuarios/${data.id}`, data);
  },

  delete: (id: string) => {
    return httpClient.delete(`/usuarios/${id}`);
  },

  toggleStatus: (id: string, motivo?: string) => {
    const body = motivo ? { motivo } : {};
    return httpClient.patch<Usuario>(`/usuarios/${id}/toggle-status`, body);
  },

  cambiarPassword: (id: string, data: CambioPasswordDTO) => {
    return httpClient.patch(`/usuarios/${id}/cambiar-password`, data);
  },

  resetPassword: (id: string, newPassword: string) => {
    return httpClient.patch(`/usuarios/${id}/reset-password`, newPassword);
  },

  marcarOnboarding: (id: string, esOnboarding: boolean) => {
    return httpClient.patch(`/usuarios/${id}/onboarding`, { esOnboarding });
  },

  marcarRequiereCambioPassword: (id: string, requiereCambioPassword: boolean) => {
    return httpClient.patch(`/usuarios/${id}/requiere-cambio-password`, { requiereCambioPassword });
  },

  reenviarCredenciales: (id: string, password: string) => {
    return httpClient.post<object>(`/usuarios/${id}/reenviar-credenciales`, { password });
  },

  asignarEstablecimientos: (data: AsignarEstablecimientosDTO) => {
    return httpClient.post(`/usuarios/${data.usuarioId}/establecimientos`, { establecimientoIds: data.establecimientoIds });
  },

  getEstablecimientos: (usuarioId: string) => {
    return httpClient.get<string[]>(`/usuarios/${usuarioId}/establecimientos`);
  },

  actualizarNombre: (id: string, nombre: string, apellido: string) => {
    return httpClient.patch<Usuario>(`/usuarios/${id}/nombre`, { nombre, apellido });
  },

  listarSesiones: async (params?: { page?: number; pageSize?: number }): Promise<SesionesPaginatedResponse> => {
    const response = await httpClient.get<SesionActiva[]>('/usuarios/sesiones', params as Record<string, string | number | boolean | undefined> | undefined);
    return {
      data: response.data || [],
      paginacion: response.paginacion || {
        page: params?.page || 1,
        pageSize: params?.pageSize || 10,
        total: 0,
        totalPages: 0,
        hasNext: false,
        hasPrevious: false
      }
    };
  },

  cerrarSesion: (id: string) => {
    return httpClient.delete(`/usuarios/sesiones/${id}`);
  },
};
